import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { SimulationState, SimulationControls, Particle, ChemicalSpeciesDefinition as LegacyChemicalSpeciesDefinition, ReactionDefinition as LegacyReactionDefinition, ReactionComponent, SpeciesGeometry, ElementProperties } from '../types/simulation';
import { v4 as uuidv4 } from 'uuid';
import { parseXyzFile } from '../utils/xyzParser';
import {
  Species,
  Reaction,
  StoichiometricCoefficient,
  ChemistryPresetJSON,
  ReactionDefinitionJSON,
  PresetSpeciesJSON,
  ReactionSpeciesJSON,
  createSpecies
} from '../types/chemistry';

const POOL_FACTOR = 1.5;

const generateInitialParticleData = (
  controls: SimulationControls,
  poolSize: number
): Omit<Particle, 'id'>[] => {
  // DEBUG: console.log('Creating initial particle pool data. Target Pool Size:', poolSize, 'Controls:', controls);

  const particlePoolData: Omit<Particle, 'id'>[] = [];

  // Use rectangular dimensions if available, otherwise fall back to reactorSize
  const width = controls.reactorWidth ?? controls.reactorSize;
  const height = controls.reactorHeight ?? controls.reactorSize;
  const depth = controls.reactorDepth ?? controls.reactorSize;

  let initialActiveCreated = 0;

  const getRandomPosition = (radius: number): [number, number, number] => {
    const safeX = (width / 2) - radius * 1.1;
    const safeY = (height / 2) - radius * 1.1;
    const safeZ = (depth / 2) - radius * 1.1;
    return [
      (Math.random() - 0.5) * safeX * 2,
      (Math.random() - 0.5) * safeY * 2,
      controls.is2DMode ? 0 : (Math.random() - 0.5) * safeZ * 2, // Z=0 in 2D mode (XY plane)
    ];
  };

  // Define default visual properties since they are removed from SpeciesDefinition
  const DEFAULT_PARTICLE_COLOR = '#FFFFFF';
  const DEFAULT_PARTICLE_RADIUS = 0.15;

  controls.chemicalSpecies.forEach(speciesDef => {
    for (let i = 0; i < speciesDef.initialCount; i++) {
      particlePoolData.push({
        particleType: speciesDef.name,
        // Use defaults for color and radius
        color: DEFAULT_PARTICLE_COLOR,
        radius: DEFAULT_PARTICLE_RADIUS,
        position: getRandomPosition(DEFAULT_PARTICLE_RADIUS), // Use default radius for positioning
        isActive: true,
      });
      initialActiveCreated++;
    }
  });

  const numInactiveToAdd = poolSize - initialActiveCreated;
  // DEBUG: console.log(`Adding ${numInactiveToAdd} inactive particles to pool.`);
  const defaultInactiveRadius = 0.1;
  const defaultInactiveColor = '#555555';
  for (let i = 0; i < numInactiveToAdd; i++) {
    particlePoolData.push({
      particleType: 'inactive',
      color: defaultInactiveColor,
      radius: defaultInactiveRadius,
      position: getRandomPosition(defaultInactiveRadius),
      isActive: false,
    });
  }

  // DEBUG: console.log(`Generated pool data total: ${particlePoolData.length}`);
  return particlePoolData;
};

const calculateCounts = (
  particles: Particle[],
  chemicalSpecies: LegacyChemicalSpeciesDefinition[]
): { particleCount: number; speciesCounts: { [speciesName: string]: number } } => {
  let activeCount = 0;
  const speciesCounts: { [speciesName: string]: number } = {};

  chemicalSpecies.forEach(species => {
    speciesCounts[species.name] = 0;
  });

  for (const p of particles) {
    if (p.isActive) {
      activeCount++;
      if (speciesCounts.hasOwnProperty(p.particleType)) {
        speciesCounts[p.particleType]++;
      }
    }
  }

  return {
    particleCount: activeCount,
    speciesCounts: speciesCounts,
  };
};

const generateParticlePool = (controls: SimulationControls): Particle[] => {
  const targetActiveCount = controls.chemicalSpecies.reduce((sum, type) => sum + type.initialCount, 0);
  const safeTargetActiveCount = Math.max(1, targetActiveCount);
  const poolSize = Math.ceil(safeTargetActiveCount * POOL_FACTOR);
  // DEBUG: console.log(`Generating particle pool: Target Active = ${targetActiveCount}, Pool Size = ${poolSize}`);

  const poolData = generateInitialParticleData(controls, poolSize);

  const pool: Particle[] = poolData.map((data) => ({
    ...data,
    id: uuidv4(),
  }));

  return pool;
};

// --- Manifest Item Types ---
export interface PresetManifestItem {
  name: string;
  file: string;
}

export interface SpeciesManifestItem {
  name: string;
  file: string;
}

export interface ReactionManifestItem {
  id: string;
  file: string;
  description?: string;
}

// --- Active Configuration Types ---

// Represents the editable coefficients for one side (reactants/products) of a reaction in the UI
export interface ActiveCoefficients {
  [speciesId: string]: number;
}

// Represents a single reaction row in the UI configuration
export interface ActiveReactionConfig {
  id: string; // Unique ID for this UI row instance (uuid)
  reactionDefId: string; // ID of the base reaction definition
  reactantsCoeff: ActiveCoefficients;
  productsCoeff: ActiveCoefficients;
  initialCounts: { [speciesId: string]: number };
}

// Holds the entire configuration being edited
export interface ActiveChemistryConfiguration {
  reactions: ActiveReactionConfig[];
  // We might add global settings here later if needed
}

// --- SimulationStore Interface Augmentation ---

// Combine existing types (if needed) with new ones
// Note: We might need to reconcile/replace LegacyChemicalSpeciesDefinition/LegacyReactionDefinition later

interface SimulationStore extends SimulationState {
  controls: SimulationControls;
  setControls: (controls: Partial<SimulationControls>) => void;
  startSimulation: () => void;
  pauseSimulation: () => void;
  resetSimulation: () => void;
  updateParticleState: (id: string, updates: Partial<Omit<Particle, 'id' | 'isActive'>>) => void;
  activateParticle: (idToActivate?: string, initialState?: Partial<Omit<Particle, 'id' | 'isActive'>>) => boolean;
  deactivateParticle: (id: string) => void;
  resetCount: number;
  editingReactionId: string | null;
  startEditingReaction: (reactionId: string) => void;
  stopEditingReaction: () => void;
  removeReactionById: (reactionId: string) => void;
  averageKineticEnergy: number;
  averageKEBySpecies: { [speciesName: string]: number };
  averageVelocitiesBySpecies: { [speciesName: string]: { x: number; y: number; z: number } };
  // Energy tracking for analysis
  totalEnergy: number;
  translationalEnergy: number;
  rotationalEnergy: number;
  initialTotalEnergy: number;
  energyRatio: number;
  thermostatEnabled: boolean;
  cameraFov: number;
  backgroundColor: string;
  setCameraFov: (fov: number) => void;
  setBackgroundColor: (color: string) => void;
  ambientLightIntensity: number;
  setAmbientLightIntensity: (intensity: number) => void;
  metalness: number;
  roughness: number;
  emissiveIntensity: number;
  setMetalness: (metalness: number) => void;
  setRoughness: (roughness: number) => void;
  setEmissiveIntensity: (intensity: number) => void;
  // Bloom settings
  bloomIntensity: number;
  bloomThreshold: number;
  bloomRadius: number;
  setBloomIntensity: (intensity: number) => void;
  setBloomThreshold: (threshold: number) => void;
  setBloomRadius: (radius: number) => void;

  // Box style
  boxStyle: 'normal' | 'hidden' | 'glass' | 'forcefield';
  setBoxStyle: (style: 'normal' | 'hidden' | 'glass' | 'forcefield') => void;
  // Viewport scaling
  viewportScaleFactor: number;
  setViewportScaleFactor: (factor: number) => void;
  dofFocusDistance: number;
  dofFocalLength: number;
  dofBokehScale: number;
  tmMiddleGrey: number;
  tmMaxLuminance: number;
  vigOffset: number;
  vigDarkness: number;
  noiseOpacity: number;
  setDofFocusDistance: (val: number) => void;
  setDofFocalLength: (val: number) => void;
  setDofBokehScale: (val: number) => void;
  setTmMiddleGrey: (val: number) => void;
  setTmMaxLuminance: (val: number) => void;
  setVigOffset: (val: number) => void;
  setVigDarkness: (val: number) => void;
  setNoiseOpacity: (val: number) => void;
  speciesGeometry: { [speciesName: string]: SpeciesGeometry | null };
  elementProperties: { [symbol: string]: ElementProperties };
  loadElementProperties: () => Promise<void>;
  speciesManifest: SpeciesManifestItem[];
  reactionsManifest: ReactionManifestItem[];
  availablePresets: PresetManifestItem[];
  availableSpecies: Species[];
  availableReactions: Reaction[];
  activeChemistryConfig: ActiveChemistryConfiguration;
  isLoadingSpeciesManifest: boolean;
  isLoadingReactionsManifest: boolean;
  isLoadingPresetsManifest: boolean;
  isLoadingReactionDefinitions: boolean;
  errorLoadingChemistryData: string | null;
  loadChemistryManifests: () => Promise<void>;
  loadReactionDefinitions: (reactionIds?: string[]) => Promise<void>;
  loadPresetIntoActiveConfig: (presetFile: string) => Promise<void>;
  addReactionToActiveConfig: (reactionDefId: string) => void;
  removeReactionFromActiveConfig: (activeReactionId: string) => void;
  updateActiveReactionCoefficients: (activeReactionId: string, speciesId: string, side: 'reactants' | 'products', newCoefficient: number) => void;
  updateActiveReactionInitialCount: (activeReactionId: string, speciesId: string, newCount: number) => void;
  addSpeciesToActiveReaction: (activeReactionId: string, speciesId: string, side: 'reactants' | 'products') => void;
  removeSpeciesFromActiveReaction: (activeReactionId: string, speciesId: string, side: 'reactants' | 'products') => void;
  clearActiveConfig: () => void;
  loadSpeciesDefinitions: () => void;
  updateSpeciesGeometry: () => Promise<void>;
  isLoadingGeometry: boolean;
  errorLoadingGeometry: string | null;
  isChemistryConfigReady: boolean;
  activeVisShader: string;
  setActiveVisShader: (shaderName: string) => void;
  fitReactorToViewport: (viewportWidth: number, viewportHeight: number, cameraDistance: number, cameraFov: number, is2D: boolean, scaleFactor?: number) => void;

  // --- New UI Feature Flags ---
  uiFeatures: {
    useNewStatistics: boolean;
    useNewReactions: boolean;
    useNewControls: boolean;
    useNewSpecies: boolean;
    useNewLayout: boolean;
  };
  setUIFeature: (feature: keyof SimulationStore['uiFeatures'], enabled: boolean) => void;
  toggleUIFeature: (feature: keyof SimulationStore['uiFeatures']) => void;

  // --- Species Focus System ---
  speciesFocus: { [speciesId: string]: { focusLevel: 'none' | 'neutral' | 'dim' | 'focus'; focusSource: 'direct' | 'reaction' | null } };
  reactionFocus: { [reactionId: string]: boolean };
  elementFocus: { [element: string]: boolean };
  focusSpecies: (speciesId: string, source?: 'direct' | 'reaction') => void;
  unfocusSpecies: (speciesId: string) => void;
  focusReaction: (reactionId: string) => void;
  unfocusReaction: (reactionId: string) => void;
  focusElement: (element: string) => void;
  unfocusElement: (element: string) => void;
  clearAllFocus: () => void;

  // --- Species Customization ---
  speciesColors: { [speciesId: string]: string };
  elementColors: { [element: string]: string };
  reactionColors: { [reactionId: string]: string };
  speciesColorMode: { [speciesId: string]: 'element' | 'species' };
  setSpeciesColor: (speciesId: string, color: string) => void;
  setElementColor: (element: string, color: string) => void;
  setReactionColor: (reactionId: string, color: string) => void;
  setSpeciesColorMode: (speciesId: string, mode: 'element' | 'species') => void;
  resetSpeciesToElementColors: (speciesId: string) => void;

  // --- Species Visual Selector ---
  getSpeciesVisual: (speciesId: string) => { color: string | null; focused: boolean; opacity: number; bloom: boolean };
}

// --- Helper Function to Sync Config to Controls --- 
const calculateControlSpeciesFromActiveConfig = (
  activeConfig: ActiveChemistryConfiguration,
  availableSpecies: Species[]
): LegacyChemicalSpeciesDefinition[] => {
  // Create a map to store aggregated initial counts per species ID
  const aggregatedCounts = new Map<string, number>();

  // Aggregate counts from all active reactions
  activeConfig.reactions.forEach(reaction => {
    Object.entries(reaction.initialCounts).forEach(([speciesId, count]) => {
      const currentAgg = aggregatedCounts.get(speciesId) || 0;
      aggregatedCounts.set(speciesId, currentAgg + count);
    });
  });

  // Map availableSpecies to the legacy format, using aggregated counts
  const controlSpecies = availableSpecies.map(species => ({
    name: species.name,
    initialCount: aggregatedCounts.get(species.id) || 0,
    xyzPath: species.xyzPath
  }));

  return controlSpecies;
};

const useSimulationStore = create<SimulationStore>()(immer((set, get) => ({
  particles: [],
  reactorSize: 10,
  isRunning: false,
  particleCount: 0,
  speciesCounts: {},
  resetCount: 0,
  editingReactionId: null,
  averageKineticEnergy: 0,
  averageKEBySpecies: {},
  averageVelocitiesBySpecies: {},
  // Energy tracking for analysis
  totalEnergy: 0,
  translationalEnergy: 0,
  rotationalEnergy: 0,
  initialTotalEnergy: 0,
  energyRatio: 1.0,
  thermostatEnabled: false,
  cameraFov: 75,
  backgroundColor: '#115080',
  ambientLightIntensity: 0.5,  // Changed from 2.0 to 0.5
  metalness: 0.6,
  roughness: 0.20,
  emissiveIntensity: 2.0,  // Increased to ensure particles bloom (needs to be well above threshold)
  // Bloom settings
  bloomIntensity: 1.5,
  bloomThreshold: 0.5,  // Lower threshold so both H and O bloom well
  bloomRadius: 0.4,

  // Box style
  boxStyle: 'normal' as 'normal' | 'hidden' | 'glass' | 'forcefield',
  // Viewport scaling
  viewportScaleFactor: 3.0,
  dofFocusDistance: 0.0,
  dofFocalLength: 0.0,
  dofBokehScale: 0.0,
  tmMiddleGrey: 0.6,
  tmMaxLuminance: 16.0,
  vigOffset: 0.15,
  vigDarkness: 0.15,
  noiseOpacity: 0.25,
  speciesGeometry: {},
  elementProperties: {},
  speciesManifest: [],
  reactionsManifest: [],
  availablePresets: [],
  availableSpecies: [],
  availableReactions: [],
  activeChemistryConfig: { reactions: [] },
  isLoadingSpeciesManifest: false,
  isLoadingReactionsManifest: false,
  isLoadingPresetsManifest: false,
  isLoadingReactionDefinitions: false,
  errorLoadingChemistryData: null,
  isLoadingGeometry: false,
  errorLoadingGeometry: null,
  isChemistryConfigReady: false,
  activeVisShader: 'PBR_InstanceColor',

  // --- New UI Feature Flags ---
  uiFeatures: {
    useNewStatistics: true,
    useNewReactions: true,
    useNewControls: true,
    useNewSpecies: true,
    useNewLayout: true,
  },

  // --- Species Focus System ---
  speciesFocus: {},
  reactionFocus: {},
  elementFocus: {},

  // --- Species Customization ---
  speciesColors: {},
  elementColors: {
    'H': '#FFFFFF',  // White for hydrogen
    'O': '#FF0000',  // Red for oxygen
    'C': '#000000',  // Black for carbon
    'N': '#0000FF',  // Blue for nitrogen
    'Cl': '#00FF00', // Green for chlorine
  },
  reactionColors: {},
  speciesColorMode: {},

  controls: {
    reactorSize: 10, // Deprecated - kept for backwards compat
    reactorWidth: undefined,  // Will be set by fitReactorToViewport
    reactorHeight: undefined, // Will be set by fitReactorToViewport
    reactorDepth: 10,         // Default depth
    chemicalSpecies: [],
    reactions: [],
    simulationSpeed: 1,
    collisionElasticity: 1.0,
    particleSpeed: 5.0,
    particleScale: 1.0,
    tilingFactor: 0,
    is2DMode: true,
    physicsQuality: 'medium' as 'high' | 'medium' | 'low',
    projectorMode: false,
  },

  loadElementProperties: async () => {
    // DEBUG: console.log('[Store] Loading element properties...');
    try {
      const response = await fetch('/Elements/elements.json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.elements) {
        // DEBUG: console.log('[Store] Element properties loaded:', data.elements);
        set({ elementProperties: data.elements });
      } else {
        console.warn('[Store] Element properties file format invalid or empty.');
        set({ elementProperties: {} }); // Set to empty if format is wrong
      }
    } catch (error) {
      console.error('[Store] Failed to load element properties:', error);
      set({ elementProperties: {} }); // Set to empty on error
    }
  },

  setControls: (newControls) => {
    // DEBUG: console.log('[Store setControls] Setting controls (NO geometry parsing):', newControls);
    let needsReset = false;

    set((state) => {
      const previousControls = state.controls;
      const updatedControls = { ...previousControls, ...newControls };

      // Check if definitions changed (triggers reset)
      if (newControls.chemicalSpecies !== undefined || newControls.reactions !== undefined) {
        console.warn("[Store setControls] Species/reactions changed. Marking for reset.");
        needsReset = true;
        // DO NOT clear speciesGeometry here - handled by updateSpeciesGeometry action
      }

      // Check if reactor dimensions changed (triggers reset)
      if (newControls.reactorSize !== undefined && newControls.reactorSize !== previousControls.reactorSize) {
        state.reactorSize = updatedControls.reactorSize;
        needsReset = true;
      }
      if (newControls.reactorWidth !== undefined && newControls.reactorWidth !== previousControls.reactorWidth) {
        needsReset = true;
      }
      if (newControls.reactorHeight !== undefined && newControls.reactorHeight !== previousControls.reactorHeight) {
        needsReset = true;
      }
      if (newControls.reactorDepth !== undefined && newControls.reactorDepth !== previousControls.reactorDepth) {
        needsReset = true;
      }

      state.controls = updatedControls;
    });

    // Trigger reset if needed (based on non-geometry changes)
    if (needsReset) {
      // DEBUG: console.log("[Store setControls] Queueing resetSimulation.");
      // Important: Reset should happen AFTER geometry is potentially updated by a separate call
      // Consider if reset needs to wait for geometry loading?
      queueMicrotask(() => get().resetSimulation());
    }
  },

  startSimulation: () => {
    // DEBUG: console.log('Starting simulation');
    set({ isRunning: true });
  },

  pauseSimulation: () => {
    // DEBUG: console.log('Pausing simulation');
    set({ isRunning: false });
  },

  resetSimulation: () => {
    // DEBUG: console.log('Resetting simulation and regenerating particle pool...');
    const currentControls = get().controls;
    const newParticlePool = generateParticlePool(currentControls);
    const initialCounts = calculateCounts(newParticlePool, currentControls.chemicalSpecies);
    set((state) => {
      state.particles = newParticlePool;
      state.isRunning = true;
      state.particleCount = initialCounts.particleCount;
      state.speciesCounts = initialCounts.speciesCounts;
      state.resetCount += 1;
    });
    // DEBUG: console.log("Initial Species Counts:", get().speciesCounts);
  },

  startEditingReaction: (reactionId) => {
    // DEBUG: console.log(`Starting to edit reaction: ${reactionId}`);
    set({ editingReactionId: reactionId });
  },

  stopEditingReaction: () => {
    // DEBUG: console.log('Stopping reaction edit');
    set({ editingReactionId: null });
  },

  removeReactionById: (reactionId) => {
    // DEBUG: console.log(`Removing reaction by ID: ${reactionId}`);
    set((state) => {
      const initialLength = state.controls.reactions.length;
      state.controls.reactions = state.controls.reactions.filter(r => r.id !== reactionId);
      if (state.controls.reactions.length < initialLength) {
        // DEBUG: console.log("Reaction removed. Triggering simulation reset.");
        queueMicrotask(() => get().resetSimulation());
      }
    });
    if (get().editingReactionId === reactionId) {
      get().stopEditingReaction();
    }
  },

  setCameraFov: (fov) => {
    // DEBUG: console.log('Setting camera FOV:', fov);
    set({ cameraFov: fov });
  },

  setBackgroundColor: (color) => {
    // DEBUG: console.log('Setting background color:', color);
    set({ backgroundColor: color });
  },

  setAmbientLightIntensity: (intensity) => {
    // DEBUG: console.log('Setting ambient light intensity:', intensity);
    set({ ambientLightIntensity: intensity });
  },

  setMetalness: (metalness) => {
    // DEBUG: console.log('Setting metalness:', metalness);
    set({ metalness });
  },

  setRoughness: (roughness) => {
    // DEBUG: console.log('Setting roughness:', roughness);
    set({ roughness });
  },

  setEmissiveIntensity: (emissiveIntensity) => {
    // DEBUG: console.log('Setting emissive intensity:', emissiveIntensity);
    set({ emissiveIntensity });
  },

  setBloomIntensity: (bloomIntensity) => {
    set({ bloomIntensity });
  },

  setBloomThreshold: (bloomThreshold) => {
    set({ bloomThreshold });
  },

  setBloomRadius: (bloomRadius) => {
    set({ bloomRadius });
  },

  setBoxStyle: (boxStyle) => {
    set({ boxStyle });
  },
  setViewportScaleFactor: (factor) => {
    set({ viewportScaleFactor: factor });
  },

  setDofFocusDistance: (val) => set({ dofFocusDistance: val }),
  setDofFocalLength: (val) => set({ dofFocalLength: val }),
  setDofBokehScale: (val) => set({ dofBokehScale: val }),
  setTmMiddleGrey: (val) => set({ tmMiddleGrey: val }),
  setTmMaxLuminance: (val) => set({ tmMaxLuminance: val }),
  setVigOffset: (val) => set({ vigOffset: val }),
  setVigDarkness: (val) => set({ vigDarkness: val }),
  setNoiseOpacity: (val) => set({ noiseOpacity: val }),

  // --- Placeholder Actions (Worker is the authority) --- 
  // These actions are placeholders to satisfy the SimulationStore interface.
  // They should NOT be called during active simulation, as the worker manages state.
  updateParticleState: (id, updates) => {
    console.warn(`[Store WARNING] updateParticleState called for ID ${id}. This function is deprecated; worker manages state.`);
    // No state mutation here
  },
  activateParticle: (idToActivate, initialState) => {
    console.warn(`[Store WARNING] activateParticle called for ID ${idToActivate ?? 'any inactive'}. This function is deprecated; worker manages state.`);
    return false; // Always return false as no particle was activated by the store
  },
  deactivateParticle: (id) => {
    console.warn(`[Store WARNING] deactivateParticle called for ID ${id}. This function is deprecated; worker manages state.`);
    // No state mutation here
  },
  // --- End Placeholder Actions --- 

  loadChemistryManifests: async () => {
    set({
      isLoadingSpeciesManifest: true,
      isLoadingReactionsManifest: true,
      isLoadingPresetsManifest: true,
      errorLoadingChemistryData: null,
    });
    // DEBUG: console.log('[Store] Loading chemistry manifests...');

    const manifestUrls = {
      species: '/structures/manifest.json',
      reactions: '/Reactions/manifest.json',
      presets: '/presets/manifest.json'
    };

    try {
      const results = await Promise.allSettled([
        fetch(manifestUrls.species).then(res => res.ok ? res.json() : Promise.reject(new Error(`Failed to load ${manifestUrls.species}: ${res.status} ${res.statusText}`))),
        fetch(manifestUrls.reactions).then(res => res.ok ? res.json() : Promise.reject(new Error(`Failed to load ${manifestUrls.reactions}: ${res.status} ${res.statusText}`))),
        fetch(manifestUrls.presets).then(res => res.ok ? res.json() : Promise.reject(new Error(`Failed to load ${manifestUrls.presets}: ${res.status} ${res.statusText}`)))
      ]);

      let errorFound: string | null = null;

      // Process Species Manifest
      if (results[0].status === 'fulfilled') {
        set({ speciesManifest: results[0].value as SpeciesManifestItem[] });
        // DEBUG: console.log('[Store] Species manifest loaded:', results[0].value);
      } else {
        console.error('[Store] Error loading species manifest:', results[0].reason);
        errorFound = results[0].reason.message || 'Failed to load species manifest';
      }
      set({ isLoadingSpeciesManifest: false });

      // Process Reactions Manifest
      if (results[1].status === 'fulfilled') {
        set({ reactionsManifest: results[1].value as ReactionManifestItem[] });
        // DEBUG: console.log('[Store] Reactions manifest loaded:', results[1].value);
      } else {
        console.error('[Store] Error loading reactions manifest:', results[1].reason);
        errorFound = errorFound || results[1].reason.message || 'Failed to load reactions manifest';
      }
      set({ isLoadingReactionsManifest: false });

      // Process Presets Manifest
      if (results[2].status === 'fulfilled') {
        set({ availablePresets: results[2].value as PresetManifestItem[] });
        // DEBUG: console.log('[Store] Presets manifest loaded:', results[2].value);
      } else {
        console.error('[Store] Error loading presets manifest:', results[2].reason);
        errorFound = errorFound || results[2].reason.message || 'Failed to load presets manifest';
      }
      set({ isLoadingPresetsManifest: false });

      if (errorFound) {
        set({ errorLoadingChemistryData: errorFound });
      } else {
        // --- Trigger loading of definitions --- 
        // Call separate actions to process the manifests into usable definitions
        queueMicrotask(() => {
          get().loadSpeciesDefinitions(); // New action to create Species objects
          // We'll load reactions after species are processed
        });
      }

    } catch (error) {
      // Catch potential network errors not caught by Promise.allSettled rejections
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred during manifest loading';
      console.error('[Store] Critical error loading chemistry manifests:', error);
      set({
        errorLoadingChemistryData: errorMessage,
        isLoadingSpeciesManifest: false,
        isLoadingReactionsManifest: false,
        isLoadingPresetsManifest: false,
      });
    }
  },
  loadReactionDefinitions: async (reactionIdsToLoad?: string[]) => {
    const manifest = get().reactionsManifest;
    const availableSpecies = get().availableSpecies;

    if (!manifest || manifest.length === 0) {
      console.warn('[Store] Reactions manifest is empty or not loaded. Cannot load reaction definitions.');
      set({ availableReactions: [], isLoadingReactionDefinitions: false });
      return;
    }
    if (!availableSpecies || availableSpecies.length === 0) {
      console.warn('[Store] Available species list is empty. Cannot process reaction definitions.');
      set({ availableReactions: [], isLoadingReactionDefinitions: false });
      // Consider setting an error state here as well?
      return;
    }

    set({ isLoadingReactionDefinitions: true, errorLoadingChemistryData: get().errorLoadingChemistryData || null }); // Clear previous reaction-specific errors if any
    // DEBUG: console.log('[Store] Loading reaction definitions...');

    // Create a map for efficient species lookup by name
    const speciesNameToIdMap = new Map<string, string>();
    availableSpecies.forEach(s => speciesNameToIdMap.set(s.name, s.id));

    // Determine which reactions to load (all from manifest or specific subset)
    const reactionsToFetch = reactionIdsToLoad
      ? manifest.filter(item => reactionIdsToLoad.includes(item.id))
      : manifest;

    if (reactionsToFetch.length === 0) {
      console.warn('[Store] No reactions selected or found in manifest to load definitions for.');
      set({ isLoadingReactionDefinitions: false });
      return;
    }

    const fetchPromises = reactionsToFetch.map(item =>
      fetch(`/Reactions/${item.file}`)
        .then(res => {
          if (!res.ok) {
            return Promise.reject(new Error(`Failed to load ${item.file}: ${res.status} ${res.statusText}`));
          }
          return res.json() as Promise<ReactionDefinitionJSON>; // Assume valid JSON structure
        })
        .catch(error => {
          // Wrap fetch/parse errors to include which file failed
          return Promise.reject(new Error(`Error processing ${item.file}: ${error.message}`));
        })
    );

    const results = await Promise.allSettled(fetchPromises);
    const loadedReactions: Reaction[] = [];
    let errorFound: string | null = get().errorLoadingChemistryData; // Keep existing errors

    results.forEach((result, index) => {
      const manifestItem = reactionsToFetch[index];
      if (result.status === 'fulfilled') {
        const reactionJson = result.value as any; // Could be new or legacy format
        try {
          // Detect new reversible format (has leftSide/rightSide)
          const isReversibleFormat = 'leftSide' in reactionJson && 'rightSide' in reactionJson;

          if (isReversibleFormat) {
            // --- New Reversible Format ---
            // Convert leftSide to StoichiometricCoefficient[]
            const leftSideCoeffs: StoichiometricCoefficient[] = reactionJson.leftSide.map((r: any) => {
              const speciesId = speciesNameToIdMap.get(r.speciesName);
              if (!speciesId) {
                throw new Error(`Unknown species "${r.speciesName}" in reaction ${reactionJson.id}`);
              }
              return { speciesId, coefficient: r.count };
            });

            // Convert rightSide to StoichiometricCoefficient[]
            const rightSideCoeffs: StoichiometricCoefficient[] = reactionJson.rightSide.map((p: any) => {
              const speciesId = speciesNameToIdMap.get(p.speciesName);
              if (!speciesId) {
                throw new Error(`Unknown species "${p.speciesName}" in reaction ${reactionJson.id}`);
              }
              return { speciesId, coefficient: p.count };
            });

            // Determine if forward direction is decay or collision based on reactant structure
            const forwardIsDecay = leftSideCoeffs.length === 1 && leftSideCoeffs[0].coefficient === 1;
            const reverseIsDecay = rightSideCoeffs.length === 1 && rightSideCoeffs[0].coefficient === 1;

            // Create FORWARD reaction (left → right)
            const forwardReaction: any = {
              id: `${reactionJson.id}_forward`,
              description: reactionJson.description ? `${reactionJson.description} (forward)` : undefined,
              reactants: leftSideCoeffs,
              products: rightSideCoeffs,
            };
            if (forwardIsDecay) {
              forwardReaction.rateConstant = reactionJson.forwardRate;
            } else {
              forwardReaction.probability = reactionJson.forwardRate;
            }
            loadedReactions.push(forwardReaction);

            // Create REVERSE reaction (right → left)
            const reverseReaction: any = {
              id: `${reactionJson.id}_reverse`,
              description: reactionJson.description ? `${reactionJson.description} (reverse)` : undefined,
              reactants: rightSideCoeffs,
              products: leftSideCoeffs,
            };
            if (reverseIsDecay) {
              reverseReaction.rateConstant = reactionJson.reverseRate;
            } else {
              reverseReaction.probability = reactionJson.reverseRate;
            }
            loadedReactions.push(reverseReaction);

            // DEBUG: console.log(`[Store] Expanded reversible reaction ${reactionJson.id} into forward (${forwardIsDecay ? 'decay' : 'collision'}) + reverse (${reverseIsDecay ? 'decay' : 'collision'})`);

          } else {
            // --- Legacy Format ---
            const reactants: StoichiometricCoefficient[] = reactionJson.reactants.map((r: any) => {
              const speciesId = speciesNameToIdMap.get(r.speciesName);
              if (!speciesId) {
                throw new Error(`Unknown reactant species name "${r.speciesName}" in reaction ${reactionJson.id || manifestItem.file}`);
              }
              return { speciesId, coefficient: r.count };
            });

            const products: StoichiometricCoefficient[] = reactionJson.products.map((p: any) => {
              const speciesId = speciesNameToIdMap.get(p.speciesName);
              if (!speciesId) {
                throw new Error(`Unknown product species name "${p.speciesName}" in reaction ${reactionJson.id || manifestItem.file}`);
              }
              return { speciesId, coefficient: p.count };
            });

            const reactionObj: any = {
              id: reactionJson.id || manifestItem.id,
              description: reactionJson.description || manifestItem.description,
              reactants,
              products,
              probability: reactionJson.probability,
            };

            if (reactionJson.rateConstant !== undefined) {
              reactionObj.rateConstant = reactionJson.rateConstant;
            }

            loadedReactions.push(reactionObj);
          }

        } catch (processingError) {
          const message = processingError instanceof Error ? processingError.message : 'Unknown processing error';
          console.error(`[Store] Error processing reaction definition from ${manifestItem.file}:`, message);
          errorFound = errorFound || message;
        }

      } else {
        // Handle fetch/parse errors reported by Promise.allSettled
        console.error('[Store] Error loading or parsing reaction definition:', result.reason);
        errorFound = errorFound || result.reason.message || `Failed to load reaction definition for ${manifestItem.file}`;
      }
    });

    // DEBUG: console.log('[Store] Available reaction definitions processed:', loadedReactions);
    // DEBUG: console.log('[Store] Decay reactions:', loadedReactions.filter((r: any) => r.rateConstant !== undefined).map((r: any) => ({ id: r.id, rateConstant: r.rateConstant })));
    // DEBUG: console.log('[Store] Collision reactions:', loadedReactions.filter((r: any) => r.probability !== undefined).map((r: any) => ({ id: r.id, probability: r.probability })));
    set({
      availableReactions: loadedReactions,
      isLoadingReactionDefinitions: false,
      errorLoadingChemistryData: errorFound,
    });
  },
  loadPresetIntoActiveConfig: async (presetFile: string) => {
    if (!presetFile) {
      console.warn('[Store] No preset file specified for loading.');
      set({ activeChemistryConfig: { reactions: [] }, isChemistryConfigReady: false });
      return;
    }

    // DEBUG: console.log(`[Store] Loading preset ${presetFile} into active configuration...`);
    set({ errorLoadingChemistryData: null, isChemistryConfigReady: false });

    const availableSpecies = get().availableSpecies;
    const availableReactions = get().availableReactions;
    const reactionsManifest = get().reactionsManifest;

    if (availableSpecies.length === 0 || availableReactions.length === 0 || reactionsManifest.length === 0) {
      const errorMsg = '[Store] Cannot load preset: Species or Reaction definitions/manifest not available.';
      console.error(errorMsg);
      set({ errorLoadingChemistryData: errorMsg, activeChemistryConfig: { reactions: [] }, isChemistryConfigReady: false });
      return;
    }

    try {
      const response = await fetch(`/presets/${presetFile}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const presetData: ChemistryPresetJSON = await response.json();

      // --- Prepare Lookups ---
      const speciesNameToIdMap = new Map<string, string>();
      const speciesIdToPresetCountMap = new Map<string, number>();
      availableSpecies.forEach(s => speciesNameToIdMap.set(s.name, s.id));
      presetData.chemicalSpecies.forEach(ps => {
        const speciesId = speciesNameToIdMap.get(ps.name);
        if (speciesId) {
          speciesIdToPresetCountMap.set(speciesId, ps.initialCount);
        } else {
          console.warn(`[Store] Species "${ps.name}" from preset ${presetFile} not found in available species list.`);
        }
      });

      const reactionDefIdToDefMap = new Map<string, Reaction>();
      availableReactions.forEach(r => reactionDefIdToDefMap.set(r.id, r));

      // Map reaction file paths (e.g., "Reactions/h_formation.json") to reaction IDs (e.g., "h_formation")
      const reactionFilePathToIdMap = new Map<string, string>();
      reactionsManifest.forEach(item => reactionFilePathToIdMap.set(`Reactions/${item.file}`, item.id));

      // --- Build Active Configuration ---
      const newActiveReactions: ActiveReactionConfig[] = [];
      const reactionFilesToLoad = presetData.reactionFiles || [];

      // Collect all unique species involved across all reactions for initialCounts
      const allInvolvedSpeciesIds = new Set<string>();

      for (const reactionFilePath of reactionFilesToLoad) {
        const baseReactionDefId = reactionFilePathToIdMap.get(reactionFilePath);
        if (!baseReactionDefId) {
          console.warn(`[Store] Could not find reaction ID for file path "${reactionFilePath}" listed in preset ${presetFile}. Skipping.`);
          continue;
        }

        // Look for both the base ID and the _forward/_reverse variants (reversible reactions get expanded)
        const forwardId = `${baseReactionDefId}_forward`;
        const reverseId = `${baseReactionDefId}_reverse`;

        // Try to find reactions: first check if there are _forward/_reverse variants, otherwise use base ID
        const forwardDef = reactionDefIdToDefMap.get(forwardId);
        const reverseDef = reactionDefIdToDefMap.get(reverseId);
        const baseDef = reactionDefIdToDefMap.get(baseReactionDefId);

        // Collect reactions to add (could be 1 base or 2 expanded variants)
        const reactionsToAdd: Reaction[] = [];
        if (forwardDef) reactionsToAdd.push(forwardDef);
        if (reverseDef) reactionsToAdd.push(reverseDef);
        if (reactionsToAdd.length === 0 && baseDef) reactionsToAdd.push(baseDef);

        if (reactionsToAdd.length === 0) {
          console.warn(`[Store] Reaction definition with ID "${baseReactionDefId}" (or variants) not found in available reactions. Skipping.`);
          continue;
        }

        // For each reaction (forward, reverse, or just base), add to active config
        for (const reactionDef of reactionsToAdd) {
          // Initialize coefficients from the definition
          const reactantsCoeff: ActiveCoefficients = {};
          reactionDef.reactants.forEach(r => { reactantsCoeff[r.speciesId] = r.coefficient; });

          const productsCoeff: ActiveCoefficients = {};
          reactionDef.products.forEach(p => { productsCoeff[p.speciesId] = p.coefficient; });

          // Collect involved species for initial counts
          const involvedSpeciesIds = new Set<string>([
            ...reactionDef.reactants.map(r => r.speciesId),
            ...reactionDef.products.map(p => p.speciesId)
          ]);

          // Initialize initial counts using defaults from preset, defaulting to 0
          const initialCounts: { [speciesId: string]: number } = {};
          involvedSpeciesIds.forEach(speciesId => {
            initialCounts[speciesId] = speciesIdToPresetCountMap.get(speciesId) ?? 0;
            allInvolvedSpeciesIds.add(speciesId);
          });

          // DEBUG: console.log(`[Store Preset] Generated initialCounts for reactionDefId ${reactionDef.id}:`, initialCounts);

          newActiveReactions.push({
            id: uuidv4(), // Unique ID for this UI instance
            reactionDefId: reactionDef.id,
            reactantsCoeff,
            productsCoeff,
            initialCounts,
          });
        }
      }

      const newActiveConfig = { reactions: newActiveReactions };
      // DEBUG: console.log(`[Store Preset] Final newActiveConfig before set state:`, JSON.parse(JSON.stringify(newActiveConfig)));

      // --- Update State (Active Config AND Controls) ---
      let calculatedControlSpecies: LegacyChemicalSpeciesDefinition[] = [];
      set(state => {
        state.activeChemistryConfig = newActiveConfig;
        calculatedControlSpecies = calculateControlSpeciesFromActiveConfig(
          newActiveConfig,
          state.availableSpecies
        );
        state.controls.chemicalSpecies = calculatedControlSpecies;
        state.errorLoadingChemistryData = null;
      });

      // Call geometry update AFTER state is set
      // DEBUG: console.log("[Store Preset] Attempting geometry update...");
      await get().updateSpeciesGeometry();
      // DEBUG: console.log("[Store Preset] Geometry update finished. Setting config ready.");

      // --- FINAL STEP: Set Ready Flag --- 
      set({ isChemistryConfigReady: true });

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error loading preset';
      console.error(`[Store] Failed to load or process preset ${presetFile}:`, error);
      set(state => {
        state.errorLoadingChemistryData = message;
        state.activeChemistryConfig = { reactions: [] };
        state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
          { reactions: [] },
          state.availableSpecies
        );
        state.isChemistryConfigReady = false;
      });
      set({ speciesGeometry: {}, isLoadingGeometry: false });
    }
  },
  addReactionToActiveConfig: (reactionDefId: string) => set((state) => {
    // DEBUG: console.log(`[Store] Adding reaction definition ${reactionDefId} to active config...`);
    const reactionDef = state.availableReactions.find(r => r.id === reactionDefId);
    if (!reactionDef) {
      console.error(`[Store] Cannot add reaction: Definition with ID ${reactionDefId} not found.`);
      return;
    }

    // Initialize coefficients from the base definition
    const reactantsCoeff: ActiveCoefficients = {};
    reactionDef.reactants.forEach(r => { reactantsCoeff[r.speciesId] = r.coefficient; });

    const productsCoeff: ActiveCoefficients = {};
    reactionDef.products.forEach(p => { productsCoeff[p.speciesId] = p.coefficient; });

    // Initialize initial counts to 0 for all involved species
    const initialCounts: { [speciesId: string]: number } = {};
    const involvedSpeciesIds = new Set<string>([
      ...reactionDef.reactants.map(r => r.speciesId),
      ...reactionDef.products.map(p => p.speciesId)
    ]);
    involvedSpeciesIds.forEach(speciesId => {
      initialCounts[speciesId] = 0;
    });

    const newActiveReaction: ActiveReactionConfig = {
      id: uuidv4(), // Unique ID for this UI instance
      reactionDefId: reactionDef.id,
      reactantsCoeff,
      productsCoeff,
      initialCounts,
    };

    // Update active config
    state.activeChemistryConfig.reactions.push(newActiveReaction);
    // DEBUG: console.log(`[Store] Reaction added. New active config:`, state.activeChemistryConfig);

    // Update controls species
    state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
      state.activeChemistryConfig,
      state.availableSpecies
    );
    // Call geometry update after state update
    queueMicrotask(() => get().updateSpeciesGeometry());
    state.isChemistryConfigReady = false;
  }),
  removeReactionFromActiveConfig: (activeReactionId: string) => set((state) => {
    // DEBUG: console.log(`[Store] Removing reaction ${activeReactionId} from active config...`);
    const initialLength = state.activeChemistryConfig.reactions.length;
    state.activeChemistryConfig.reactions = state.activeChemistryConfig.reactions.filter(
      r => r.id !== activeReactionId
    );
    if (state.activeChemistryConfig.reactions.length < initialLength) {
      // DEBUG: console.log(`[Store] Reaction removed. New active config:`, state.activeChemistryConfig);
      // Update controls species
      state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
        state.activeChemistryConfig,
        state.availableSpecies
      );
    } else {
      console.warn(`[Store] Reaction with UI ID ${activeReactionId} not found in active config.`);
    }
    // Call geometry update only if something changed
    queueMicrotask(() => get().updateSpeciesGeometry());
    state.isChemistryConfigReady = false;
  }),
  updateActiveReactionCoefficients: (activeReactionId, speciesId, side, newCoefficient) => set((state) => {
    // DEBUG: console.log(`[Store Action] updateActiveReactionCoefficients called:`, { activeReactionId, speciesId, side, newCoefficient });
    const reactionIndex = state.activeChemistryConfig.reactions.findIndex(r => r.id === activeReactionId);
    if (reactionIndex === -1) {
      console.warn("[Store Action] Reaction not found.");
      return;
    }
    const targetCoeffs = side === 'reactants' ? state.activeChemistryConfig.reactions[reactionIndex].reactantsCoeff : state.activeChemistryConfig.reactions[reactionIndex].productsCoeff;
    if (!targetCoeffs.hasOwnProperty(speciesId)) {
      console.warn(`[Store Action] Species ${speciesId} not found on ${side} side.`);
      return;
    }
    const coefficient = Math.max(0, Math.floor(newCoefficient));
    // DEBUG: console.log(`[Store Action] Current ${side} Coeff:`, targetCoeffs[speciesId], ` | New Coeff: ${coefficient}`);
    targetCoeffs[speciesId] = coefficient;
    // DEBUG: console.log(`[Store Action] State updated for reaction:`, JSON.parse(JSON.stringify(state.activeChemistryConfig.reactions[reactionIndex]))); // Deep copy for logging
  }),
  updateActiveReactionInitialCount: (activeReactionId, speciesId, newCount) => set((state) => {
    // DEBUG: console.log(`[Store Action] updateActiveReactionInitialCount called:`, { activeReactionId, speciesId, newCount });
    const reactionIndex = state.activeChemistryConfig.reactions.findIndex(r => r.id === activeReactionId);
    if (reactionIndex === -1) {
      console.warn("[Store Action] Reaction not found.");
      return;
    }
    const targetReaction = state.activeChemistryConfig.reactions[reactionIndex];
    if (!targetReaction.reactantsCoeff.hasOwnProperty(speciesId) && !targetReaction.productsCoeff.hasOwnProperty(speciesId)) {
      console.warn(`[Store Action] Species ${speciesId} not found in reaction.`);
      return;
    }
    const count = Math.max(0, Math.floor(newCount));
    // DEBUG: console.log(`[Store Action] Current Initial Count:`, targetReaction.initialCounts[speciesId], ` | New Count: ${count}`);
    targetReaction.initialCounts[speciesId] = count;
    // DEBUG: console.log(`[Store Action] State updated for reaction counts:`, JSON.parse(JSON.stringify(targetReaction.initialCounts)));

    // Update controls species since initial counts changed
    // DEBUG: console.log("[Store Action] Recalculating control species...");
    state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
      state.activeChemistryConfig,
      state.availableSpecies
    );
    queueMicrotask(() => get().updateSpeciesGeometry());
  }),
  addSpeciesToActiveReaction: (activeReactionId, speciesId, side) => set((state) => {
    const reactionIndex = state.activeChemistryConfig.reactions.findIndex(r => r.id === activeReactionId);
    if (reactionIndex === -1) return;
    // Check if the species exists in the master list
    const speciesExists = state.availableSpecies.some(s => s.id === speciesId);
    if (!speciesExists) {
      console.warn(`[Store] Cannot add species: Species with ID ${speciesId} not found in availableSpecies.`);
      return;
    }

    const targetReaction = state.activeChemistryConfig.reactions[reactionIndex];
    const coefficients = side === 'reactants' ? targetReaction.reactantsCoeff : targetReaction.productsCoeff;

    if (coefficients.hasOwnProperty(speciesId)) {
      console.warn(`[Store] Species ${speciesId} already exists in ${side} of reaction ${activeReactionId}.`);
      return; // Already present
    }

    // DEBUG: console.log(`[Store] Adding species ${speciesId} to ${side} of reaction ${activeReactionId}`);

    // Add species with default coefficient 1
    coefficients[speciesId] = 1;

    // Ensure initial count exists, defaulting to 0
    if (!targetReaction.initialCounts.hasOwnProperty(speciesId)) {
      targetReaction.initialCounts[speciesId] = 0;
    }
    // DEBUG: console.log(`[Store] Species added. Config:`, state.activeChemistryConfig.reactions[reactionIndex]);

    // This action might add a species with a default initial count (0). 
    // To be safe, let's update the controls.
    state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
      state.activeChemistryConfig,
      state.availableSpecies
    );
    queueMicrotask(() => get().updateSpeciesGeometry());
    state.isChemistryConfigReady = false;
  }),
  removeSpeciesFromActiveReaction: (activeReactionId, speciesId, side) => set((state) => {
    const reactionIndex = state.activeChemistryConfig.reactions.findIndex(r => r.id === activeReactionId);
    if (reactionIndex === -1) return;
    const targetReaction = state.activeChemistryConfig.reactions[reactionIndex];
    const coefficients = side === 'reactants' ? targetReaction.reactantsCoeff : targetReaction.productsCoeff;

    if (!coefficients.hasOwnProperty(speciesId)) {
      console.warn(`[Store] Cannot remove species: Species ${speciesId} not found in ${side} of reaction ${activeReactionId}.`);
      return; // Not present on this side
    }

    // DEBUG: console.log(`[Store] Removing species ${speciesId} from ${side} of reaction ${activeReactionId}`);

    // Remove from the specified side
    delete coefficients[speciesId];

    // Check if it still exists on the *other* side
    const otherSideCoefficients = side === 'reactants' ? targetReaction.productsCoeff : targetReaction.reactantsCoeff;
    if (!otherSideCoefficients.hasOwnProperty(speciesId)) {
      // If not present on the other side either, remove from initialCounts
      // DEBUG: console.log(`[Store] Species ${speciesId} no longer in reaction ${activeReactionId}, removing from initial counts.`);
      delete targetReaction.initialCounts[speciesId];
    }
    // DEBUG: console.log(`[Store] Species removed. Config:`, state.activeChemistryConfig.reactions[reactionIndex]);

    // Initial counts might have changed, update controls.
    state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
      state.activeChemistryConfig,
      state.availableSpecies
    );
    queueMicrotask(() => get().updateSpeciesGeometry());
    state.isChemistryConfigReady = false;
  }),
  clearActiveConfig: () => set((state) => {
    // DEBUG: console.log('[Store] Clearing active chemistry configuration...');
    state.activeChemistryConfig.reactions = [];
    // Update controls species based on empty config
    state.controls.chemicalSpecies = calculateControlSpeciesFromActiveConfig(
      state.activeChemistryConfig,
      state.availableSpecies
    );
    state.isChemistryConfigReady = false;
    queueMicrotask(() => get().updateSpeciesGeometry());
  }),
  loadSpeciesDefinitions: () => {
    // DEBUG: console.log('[Store] Processing species manifest into definitions...');
    const manifest = get().speciesManifest;
    if (!manifest || manifest.length === 0) {
      console.warn('[Store] Species manifest is empty or not loaded. Cannot create species definitions.');
      set({ availableSpecies: [] }); // Ensure it's empty
      return;
    }

    try {
      const speciesDefinitions: Species[] = manifest.map(item => ({
        id: `species-${item.name}`, // Deterministic ID based on name
        name: item.name,
        xyzPath: `/structures/${item.file}` // Construct full path
        // Add default color or other properties here if needed
      }));

      set({ availableSpecies: speciesDefinitions });
      // DEBUG: console.log('[Store] Available species definitions created:', speciesDefinitions);

      // --- Now trigger reaction definition loading --- 
      queueMicrotask(() => {
        get().loadReactionDefinitions();
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred creating species definitions';
      console.error('[Store] Error processing species manifest:', error);
      set({
        errorLoadingChemistryData: get().errorLoadingChemistryData || errorMessage,
        availableSpecies: [] // Clear potentially partial data
      });
    }
  },
  updateSpeciesGeometry: async () => {
    const currentSpecies = get().controls.chemicalSpecies; // Get species from controls
    // DEBUG: console.log("[Store updateSpeciesGeometry] Updating geometry for:", JSON.stringify(currentSpecies));

    if (!currentSpecies || currentSpecies.length === 0) {
      console.warn("[Store updateSpeciesGeometry] No species defined, skipping.");
      set({ speciesGeometry: {}, isLoadingGeometry: false, errorLoadingGeometry: null, isChemistryConfigReady: false }); // Ensure ready is false
      return;
    }

    set({ isLoadingGeometry: true, errorLoadingGeometry: null, speciesGeometry: {} }); // Set loading, clear previous geometry
    let errorFound: string | null = null;

    const geometryPromises = currentSpecies.map(async (species) => {
      if (species.xyzPath) {
        try {
          // DEBUG: console.log(`[Store updateSpeciesGeometry] Parsing ${species.name} from ${species.xyzPath}`);
          const geometry = await parseXyzFile(species.xyzPath);
          // DEBUG: console.log(`[Store updateSpeciesGeometry] Parsed ${species.name}:`, geometry ? 'OK' : 'null');
          return { name: species.name, geometry };
        } catch (parseError) {
          const msg = parseError instanceof Error ? parseError.message : String(parseError);
          console.error(`[Store updateSpeciesGeometry] Error parsing ${species.name}:`, msg);
          errorFound = errorFound || `Failed to parse ${species.name}: ${msg}`; // Capture first error
          return { name: species.name, geometry: null };
        }
      } else {
        // DEBUG: console.log(`[Store updateSpeciesGeometry] No path for ${species.name}`);
        return { name: species.name, geometry: null };
      }
    });

    try {
      const results = await Promise.all(geometryPromises);
      // DEBUG: console.log("[Store updateSpeciesGeometry] Parsing results received:", results);
      const newGeometryMap: { [speciesName: string]: SpeciesGeometry | null } = {};
      results.forEach(result => {
        if (result) {
          newGeometryMap[result.name] = result.geometry;
        }
      });
      // DEBUG: console.log("[Store updateSpeciesGeometry] Setting final geometry map state:", JSON.stringify(newGeometryMap));

      // --- Determine ready state based on errors --- 
      const ready = !errorFound; // Ready if no parsing errors occurred
      // DEBUG: console.log(`[Store updateSpeciesGeometry] Geometry processing finished. Setting state: isLoading=false, error=${errorFound}, isReady=${ready}`);
      set({
        speciesGeometry: newGeometryMap,
        isLoadingGeometry: false,
        errorLoadingGeometry: errorFound, // Set error if any occurred during parsing
        isChemistryConfigReady: ready // Set ready state based on parsing success
      });
    } catch (criticalError) { // Catch error from Promise.all itself
      console.error("[Store updateSpeciesGeometry] Critical error in Promise.all:", criticalError);
      const msg = criticalError instanceof Error ? criticalError.message : String(criticalError);
      set({
        speciesGeometry: {}, // Clear geometry
        isLoadingGeometry: false,
        errorLoadingGeometry: errorFound || `Critical error during geometry processing: ${msg}`,
        isChemistryConfigReady: false // Ensure ready is false on critical error
      });
    }
  },
  setActiveVisShader: (shaderName: string) => set({ activeVisShader: shaderName }),

  // Calculate and set reactor dimensions to EXACTLY match viewport
  // Goal: Reactor edges align perfectly with screen edges
  fitReactorToViewport: (viewportWidth: number, viewportHeight: number, cameraDistance: number, cameraFov: number, is2D: boolean, scaleFactor?: number) => {
    // Use provided scaleFactor or get from store
    const scale = scaleFactor ?? get().viewportScaleFactor;
    // DEBUG: console.log('[Store] Fitting reactor to viewport:', { viewportWidth, viewportHeight, cameraDistance, cameraFov, is2D, scaleFactor: scale });

    // Calculate visible height and width at the reactor's distance from camera
    const vFov = (cameraFov * Math.PI) / 180; // Convert to radians
    const visibleHeight = 2 * Math.tan(vFov / 2) * cameraDistance;
    const visibleWidth = visibleHeight * (viewportWidth / viewportHeight);

    // DEBUG: console.log('[Store] Visible dimensions at camera distance (world units):', { visibleWidth, visibleHeight });

    // Scale down the visible dimensions to get reasonable simulation units
    // Lower scaleFactor = bigger simulation units = particles appear larger relative to box
    const width = visibleWidth / scale;
    const height = visibleHeight / scale;

    let depth: number;
    if (is2D) {
      // Calculate depth based on largest molecule (110% of max Z dimension)
      const state = get();
      let maxMoleculeDepth = 0;

      // Find the maximum Z dimension across all loaded species geometries
      Object.values(state.speciesGeometry).forEach(geometry => {
        if (geometry?.atoms) {
          geometry.atoms.forEach(atom => {
            const z = Math.abs(atom.position[2]); // Z coordinate
            maxMoleculeDepth = Math.max(maxMoleculeDepth, z);
          });
        }
      });

      // If no geometry loaded, use a reasonable default
      if (maxMoleculeDepth === 0) {
        maxMoleculeDepth = 2; // Default molecule depth
      }

      // Set depth to accommodate molecule Z extent, with a minimum floor
      // to prevent particles escaping when geometry hasn't loaded yet
      const calculatedDepth = maxMoleculeDepth * 2.2; // 110% on each side
      const minimumDepth = 2; // Minimum depth to prevent boundary issues
      depth = Math.max(calculatedDepth, minimumDepth);
      // DEBUG: console.log('[Store] 2D mode: max molecule depth:', maxMoleculeDepth, 'box depth:', depth);
    } else {
      depth = 10; // Normal 3D depth
    }

    // DEBUG: console.log('[Store] Setting reactor dimensions (simulation units):', { width, height, depth });

    // Update reactor dimensions
    get().setControls({
      reactorWidth: Math.round(width),
      reactorHeight: Math.round(height),
      reactorDepth: depth,
      reactorSize: Math.max(width, height) // Keep reactorSize for backwards compat
    });
  },

  // --- New UI Feature Flag Actions ---
  setUIFeature: (feature, enabled) => {
    // DEBUG: console.log(`[Store] Setting UI feature ${feature} to ${enabled}`);
    set((state) => {
      state.uiFeatures[feature] = enabled;
    });
  },

  toggleUIFeature: (feature) => {
    const currentValue = get().uiFeatures[feature];
    // DEBUG: console.log(`[Store] Toggling UI feature ${feature} from ${currentValue} to ${!currentValue}`);
    set((state) => {
      state.uiFeatures[feature] = !currentValue;
    });
  },

  // --- Species Focus System Actions ---
  focusSpecies: (speciesId, source = 'direct') => {
    // DEBUG: console.log(`[Store] Cycling focus for species ${speciesId} with source ${source}`);
    set((state) => {
      const current = state.speciesFocus[speciesId];
      if (!current) {
        // First click: focus
        state.speciesFocus[speciesId] = { focusLevel: 'focus', focusSource: source };
      } else {
        // Cycle through: focus -> neutral -> dim -> none -> focus
        const cycle = ['focus', 'neutral', 'dim', 'none'] as const;
        const currentIndex = cycle.indexOf(current.focusLevel);
        const nextIndex = (currentIndex + 1) % cycle.length;
        state.speciesFocus[speciesId] = { focusLevel: cycle[nextIndex], focusSource: source };
      }
      // DEBUG: console.log(`[Store] After focus - speciesFocus:`, state.speciesFocus);
    });
  },

  unfocusSpecies: (speciesId) => {
    // DEBUG: console.log(`[Store] Unfocusing species ${speciesId}`);
    set((state) => {
      if (state.speciesFocus[speciesId]) {
        state.speciesFocus[speciesId].focusLevel = 'none';
        state.speciesFocus[speciesId].focusSource = null;
      }
    });
  },

  focusReaction: (reactionId) => {
    // DEBUG: console.log(`[Store] Focusing reaction ${reactionId}`);
    const stateNow = get();
    const target = stateNow.activeChemistryConfig.reactions.find(r => r.id === reactionId);
    if (!target) return;

    // Species set for the target reaction
    const targetSpecies = new Set<string>([
      ...Object.keys(target.reactantsCoeff),
      ...Object.keys(target.productsCoeff)
    ]);

    set((state) => {
      // Unfocus all other reactions and remove their reaction-sourced species focus
      Object.entries(state.reactionFocus).forEach(([rid, focused]) => {
        if (focused && rid !== reactionId) {
          const other = state.activeChemistryConfig.reactions.find(r => r.id === rid);
          if (other) {
            const otherSpecies = new Set<string>([
              ...Object.keys(other.reactantsCoeff),
              ...Object.keys(other.productsCoeff)
            ]);
            // For species that are not part of the new target reaction, drop reaction-sourced focus
            otherSpecies.forEach(speciesId => {
              if (!targetSpecies.has(speciesId) && state.speciesFocus[speciesId]?.focusSource === 'reaction') {
                state.speciesFocus[speciesId].focusLevel = 'none';
                state.speciesFocus[speciesId].focusSource = null;
              }
            });
          }
          state.reactionFocus[rid] = false;
        }
      });

      // Focus this reaction
      state.reactionFocus[reactionId] = true;
      targetSpecies.forEach(speciesId => {
        state.speciesFocus[speciesId] = { focusLevel: 'focus', focusSource: 'reaction' };
        // DEBUG: console.log(`[Store] Focusing species ${speciesId} from reaction ${reactionId}`);
      });
    });
  },

  unfocusReaction: (reactionId) => {
    // DEBUG: console.log(`[Store] Unfocusing reaction ${reactionId}`);
    const state = get();
    const reaction = state.activeChemistryConfig.reactions.find(r => r.id === reactionId);
    if (reaction) {
      // Unfocus only species that were focused by this reaction
      const allSpeciesIds = new Set([
        ...Object.keys(reaction.reactantsCoeff),
        ...Object.keys(reaction.productsCoeff)
      ]);

      set((state) => {
        // Mark reaction as unfocused
        state.reactionFocus[reactionId] = false;

        // Unfocus only species that were focused by this reaction
        allSpeciesIds.forEach(speciesId => {
          if (state.speciesFocus[speciesId]?.focusSource === 'reaction') {
            state.speciesFocus[speciesId].focusLevel = 'none';
            state.speciesFocus[speciesId].focusSource = null;
          }
        });
      });
    }
  },

  focusElement: (element) => {
    // DEBUG: console.log(`[Store] Focusing element ${element}`);
    set((state) => {
      state.elementFocus[element] = true;
    });
  },

  unfocusElement: (element) => {
    // DEBUG: console.log(`[Store] Unfocusing element ${element}`);
    set((state) => {
      delete state.elementFocus[element];
    });
  },

  clearAllFocus: () => {
    // DEBUG: console.log('[Store] Clearing all focus');
    set((state) => {
      state.speciesFocus = {};
      state.reactionFocus = {};
      state.elementFocus = {};
    });
  },

  // --- Species Customization Actions ---
  setSpeciesColor: (speciesId, color) => {
    // DEBUG: console.log(`[Store] Setting species ${speciesId} color to ${color}`);
    set((state) => {
      state.speciesColors[speciesId] = color;
      state.speciesColorMode[speciesId] = 'species';
    });
  },

  setElementColor: (element, color) => {
    // DEBUG: console.log(`[Store] Setting element ${element} color to ${color}`);
    set((state) => {
      state.elementColors[element] = color;
    });
  },

  setReactionColor: (reactionId, color) => {
    // DEBUG: console.log(`[Store] Setting reaction ${reactionId} color to ${color}`);
    set((state) => {
      state.reactionColors[reactionId] = color;
    });
  },

  setSpeciesColorMode: (speciesId, mode) => {
    // DEBUG: console.log(`[Store] Setting species ${speciesId} color mode to ${mode}`);
    set((state) => {
      state.speciesColorMode[speciesId] = mode;
    });
  },

  resetSpeciesToElementColors: (speciesId) => {
    // DEBUG: console.log(`[Store] Resetting species ${speciesId} to element colors`);
    set((state) => {
      delete state.speciesColors[speciesId];
      delete state.speciesColorMode[speciesId];
    });
  },

  // --- Species Visual Selector Implementation ---
  getSpeciesVisual: (speciesId) => {
    const state = get();
    const species = state.availableSpecies.find(s => s.id === speciesId);
    if (!species) {
      return { color: '#ffffff', focused: false, opacity: 1.0, bloom: false };
    }

    // Determine focus level
    const focusLevel = state.speciesFocus[speciesId]?.focusLevel || 'none';
    const isFocused = focusLevel === 'focus';

    let effectiveColor: string | null = '#ffffff'; // default white

    if (isFocused) {
      // IF FOCUSED: Use hierarchy colors (Reaction > Species > Element)

      // Check for reaction color first (highest priority)
      const involvedReactions = state.activeChemistryConfig.reactions.filter(r =>
        Object.keys(r.reactantsCoeff).includes(speciesId) ||
        Object.keys(r.productsCoeff).includes(speciesId)
      );

      for (const reaction of involvedReactions) {
        if (state.reactionFocus[reaction.id] && state.reactionColors[reaction.id]) {
          effectiveColor = state.reactionColors[reaction.id];
          break;
        }
      }

      // If no reaction color, check species color
      if (effectiveColor === '#ffffff' && state.speciesColors[speciesId]) {
        effectiveColor = state.speciesColors[speciesId];
      }

      // If still default, check element colors
      if (effectiveColor === '#ffffff') {
        // Extract elements from species name (simple approach)
        const elementMatch = species.name.match(/[A-Z][a-z]?/g);
        if (elementMatch && elementMatch.length > 0) {
          const primaryElement = elementMatch[0];
          if (state.elementColors[primaryElement]) {
            effectiveColor = state.elementColors[primaryElement];
          }
        }
      }
    } else {
      // IF NOT FOCUSED: Use DEFAULT colors from JSON files (element properties)
      // Return null to signal the renderer should use elementProps.color
      effectiveColor = null;
    }

    // Calculate visual properties based on focus level
    let opacity: number;
    let bloom: boolean;

    switch (focusLevel) {
      case 'focus':
        opacity = 1.0;
        bloom = true;
        break;
      case 'neutral':
        opacity = 1.0;
        bloom = false;
        break;
      case 'dim':
        opacity = 0.3;
        bloom = false;
        break;
      case 'none':
      default:
        opacity = 0.3;
        bloom = false;
        break;
    }

    return {
      color: effectiveColor,
      focused: isFocused,
      opacity,
      bloom
    };
  },
})));

// --- REMOVE Trigger initial parsing (Now handled by AppLoader) --- 
/*
const storeInstance = useSimulationStore.getState();
storeInstance.loadElementProperties(); // Moved element loading to App.tsx useEffect
storeInstance.setControls(storeInstance.controls);
// DEBUG: console.log("[SimulationStore] Store created and initial setControls triggered.");
*/
// DEBUG: console.log("[SimulationStore] Store definition complete."); // Optional: Log store definition completion

export default useSimulationStore; 